import { IS_PREVIEW, SANDBOX_PAYMENTS_ENABLED } from '../lib/environment';
import { Suspense, useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation, useNavigationType, useParams } from 'react-router-dom';
import ReactGA from 'react-ga4';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import { applyDefaultSeo, normalizePathname } from '../lib/seo';
import { dealLandingPath } from '../lib/dealShare';

const GA_MEASUREMENT_ID = 'G-HNZ5PD6RVB';

if (!IS_PREVIEW && typeof window !== 'undefined') {
  ReactGA.initialize(GA_MEASUREMENT_ID);
}

const KNOWN_PATHS = new Set([
  '/',
  // Retired landing page: a 301 in public/_redirects (and Tools.js in the app) sends it to the Wine Tracker.
  '/tools',
  '/tools/qfwine',
  '/tools/qfmarketplace',
  '/tools/reward-sc-calculator',
  '/games',
  '/games/deanair',
  '/games/skyops',
  '/games/whats-the-point',
  '/games/whats-the-point-v2',
  '/games/iata-dash',
  '/posts',
  '/contact',
  '/tos',
  '/pp',
  '/signup',
  '/setup-account',
  '/signin',
  '/cap',
  '/cap/confirmation',
]);

// Shared deal links (/d/{source}/{key}) open the matching tracker.
const isDealLinkPath = (pathname) => pathname.startsWith('/d/');

function isKnownPath(pathname) {
  return (
    KNOWN_PATHS.has(pathname) ||
    isDealLinkPath(pathname) ||
    pathname.startsWith('/posts/') ||
    pathname === '/account' ||
    pathname.startsWith('/account/')
  );
}

function getLayoutConfig(pathname) {
  const isHome = pathname === '/';
  const isTracker = (pathname.startsWith('/tools/') && pathname !== '/tools') ||
    (pathname.startsWith('/games/') && pathname !== '/games') ||
    isDealLinkPath(pathname);
  const isSetup = pathname.startsWith('/setup-account');
  const isAuth = pathname.startsWith('/signin') || pathname.startsWith('/signup');
  const isAccount = pathname === '/account' || pathname.startsWith('/account/');
  const isCap = pathname === '/cap' || pathname === '/cap/confirmation';
  const isNotFound = !isKnownPath(pathname);
  const isContent =
    pathname === '/games' ||
    pathname === '/contact' ||
    pathname === '/posts' ||
    pathname.startsWith('/posts/') ||
    pathname === '/tos' ||
    pathname === '/pp';

  const isReading = pathname === '/tos' || pathname === '/pp';

  const isAppWide = isTracker || isSetup || isAccount || isAuth;

  return {
    barePanel: isContent || isTracker || isSetup || isAuth || isAccount || isCap || isNotFound,
    flushContent: isContent && !isHome,
    wrapClass: isReading ? 'ff-route-wrap ff-route-wrap--reading' : isAppWide ? 'ff-route-wrap ff-route-wrap--wide' : 'ff-route-wrap',
  };
}

function getSeoConfig(pathname) {
  if (pathname === '/') {
    return {
      title: 'FlightFormula | Points Tools, Deals, and Travel Guides',
      description:
        'FlightFormula helps you maximise Qantas points with deal trackers, reward calculators, and practical travel-hacking guides.',
    };
  }

  if (pathname === '/tools/qfwine') {
    return {
      title: 'Qantas Wine Tracker',
      description:
        'Track live Qantas Wine bonus point deals with points-per-dollar and cents-per-point comparisons.',
    };
  }

  if (pathname === '/tools/qfmarketplace') {
    return {
      title: 'Qantas Marketplace Tracker',
      description:
        'Track Qantas Marketplace offers with standard 3 points per $1 base earn, and compare total points (base + bonus) vs bonus-only points.',
    };
  }

  if (isDealLinkPath(pathname)) {
    return {
      title: 'Qantas points deal',
      description: 'A Qantas Wine or Marketplace deal tracked by FlightFormula.',
      robots: 'noindex,follow',
    };
  }

  if (pathname === '/tools/reward-sc-calculator') {
    return {
      title: 'Status Credit Calculator',
      description:
        'Estimate Status Credits on eligible Qantas Classic Reward flights with cabin and leg-based calculations.',
    };
  }

  if (pathname === '/games') {
    return {
      title: 'Games',
      description: 'Browser-based aviation games on FlightFormula. No installs, no accounts.',
    };
  }

  if (pathname === '/games/iata-dash') {
    return {
      title: 'IATA Dash - Airport Code Quiz',
      description: 'Guess the city behind each airport code in this quick browser-based aviation quiz.',
    };
  }

  if (pathname === '/games/skyops') {
    return {
      title: 'Sky Ops — Airline Clicker Game',
      description: 'Build an airline empire in this browser-based clicker game. Buy planes, hire staff, weather events, and prestige.',
    };
  }

  if (pathname === '/games/deanair') {
    return {
      title: 'DeanAir — Airline Loyalty Incremental Game',
      description: 'Grow DeanAir from a tiny airline into a DeanMiles loyalty program that tries to denominate the world economy.',
    };
  }

  if (pathname === '/games/whats-the-point') {
    return {
      title: "What's The Point?! — DeanAir World Takeover Simulator",
      description: 'Start as a tiny airline and escalate DeanAir into a Paperclips-style DeanMiles world conquest machine.',
    };
  }

  if (pathname === '/games/whats-the-point-v2') {
    return {
      title: "What's The Point V2 — DeanMiles Paperclips Game",
      description: 'Sell tickets manually, build DeanAir, over-monetize DeanMiles, monopolize air travel, and convert all 195 countries.',
    };
  }

  if (pathname === '/posts') {
    return {
      title: 'Posts',
      description:
        'Read FlightFormula articles covering points strategy, travel hacks, reviews, and frequent-flyer news.',
      ogType: 'article',
    };
  }

  if (pathname.startsWith('/posts/')) {
    return {
      title: 'Post',
      description: 'FlightFormula travel and points article.',
      ogType: 'article',
    };
  }

  if (pathname === '/contact') {
    return {
      title: 'Contact',
      description: 'Contact FlightFormula for feedback, partnerships, and sponsorship enquiries.',
    };
  }

  if (pathname === '/setup-account' || pathname === '/signup') {
    return {
      title: 'Set Up Account',
      description: 'Set up your FlightFormula account and Wine Tracker Plus subscription options.',
      robots: 'noindex,follow',
    };
  }

  if (pathname === '/signin' || pathname === '/account' || pathname.startsWith('/account/')) {
    return {
      title: pathname === '/signin' ? 'Sign In' : 'Account',
      description: 'FlightFormula account access.',
      robots: 'noindex,follow',
    };
  }

  if (pathname === '/cap') {
    return {
      title: 'FlightFormula Cap',
      description: 'Buy the FlightFormula cap with AUD pricing and secure Stripe checkout.',
    };
  }

  if (pathname === '/cap/confirmation') {
    return {
      title: 'Order Confirmation',
      description: 'FlightFormula order confirmation and next steps.',
      robots: 'noindex,nofollow',
    };
  }

  if (pathname === '/tos') {
    return {
      title: 'Terms of Service',
      description: 'FlightFormula terms of service.',
    };
  }

  if (pathname === '/pp') {
    return {
      title: 'Privacy Policy',
      description: 'FlightFormula privacy policy and data handling summary.',
    };
  }

  return {
    title: 'FlightFormula',
    description:
      'FlightFormula tools and guides for Qantas points, reward flights, wine and marketplace deal tracking.',
  };
}

const Layout = () => {
  const location = useLocation();
  const pathname = normalizePathname(location.pathname);
  const navigationType = useNavigationType();
  const previousPath = useRef(pathname);
  const mainRef = useRef(null);
  const layout = getLayoutConfig(pathname);

  useEffect(() => {
    const changed = previousPath.current !== pathname;
    previousPath.current = pathname;
    // Keep browser Back/Forward restoration and article anchor links intact.
    if (!changed || navigationType === 'POP' || location.hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    mainRef.current?.focus({ preventScroll: true });
  }, [pathname, navigationType, location.hash]);

  useEffect(() => {
    const seo = getSeoConfig(pathname);
    applyDefaultSeo({ ...seo, canonicalPath: pathname });
  }, [pathname]);

  useEffect(() => {
    if (IS_PREVIEW) return;
    ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
  }, [location.pathname, location.search]);

  return (
    <div className="ff-site-shell">
      <a className="ff-skip-link" href="#main-content">Skip to content</a>
      <Header />
      {IS_PREVIEW && (
        <div role="status" className="ff-notice ff-preview-notice">
          Development environment: separate test accounts and data. Automatic email and SMS delivery is disabled.
          {SANDBOX_PAYMENTS_ENABLED ? ' Payments use the Stripe sandbox.' : ' Checkout and billing are disabled.'}
        </div>
      )}
      <main id="main-content" ref={mainRef} tabIndex={-1} className="ff-main">
        <div className={layout.wrapClass}>
          <div className={`ff-route-panel${layout.barePanel ? ' ff-route-panel--bare' : ''}`}>
            <div className={`ff-route-content ff-themed-route${layout.flushContent ? ' ff-route-content--flush' : ''}`}>
              <Suspense fallback={<LoadingSpinner />}>
                <Outlet />
              </Suspense>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

// /d/{source}/{key}: replace the shared link with the tracker, which finds,
// expands and highlights the deal (?deal=KEY). Routed in index.js as
// <Layout.DealLinkRedirect />.
const DealLinkRedirect = () => {
  const { source, key } = useParams();
  return <Navigate to={dealLandingPath(source, key)} replace />;
};

Layout.DealLinkRedirect = DealLinkRedirect;

export default Layout;
